import { useSuspenseQuery } from "@apollo/client/react";
import type { CompoundRecordVersionsBoolExp, CompoundRecordVersionsOrderBy, OrderBy } from "~/common/types/graphql";
import { useAuthorization } from "~/features/authz/authorization";
import {
  GetCompoundRecordVersionsForViewerDocument,
  type GetCompoundRecordVersionsForViewerQuery,
  GetCompoundRecordVersionsListDocument,
  type GetCompoundRecordVersionsListQuery,
  type GetCompoundRecordVersionsListQueryVariables,
} from "~/features/compoundRecordVersions/gqls";
import type {
  CompoundRecordVersionSearchCondition,
  CompoundRecordVersionSortField,
  SortDirection,
} from "~/features/compoundRecordVersions/types";

const buildWhere = (conditions: readonly CompoundRecordVersionSearchCondition[]): CompoundRecordVersionsBoolExp => {
  const andList: CompoundRecordVersionsBoolExp[] = [];
  for (const c of conditions) {
    const value = c.value.trim();
    if (!value) continue;
    switch (c.field) {
      case "id":
        andList.push({ id: { _eq: value } });
        break;
      case "version":
        andList.push({ version: c.exactMatch ? { _eq: value } : { _ilike: `%${value}%` } });
        break;
    }
  }
  return { _and: andList };
};

interface UseCompoundRecordVersionListArgs {
  conditions: readonly CompoundRecordVersionSearchCondition[];
  sortField: CompoundRecordVersionSortField;
  direction: SortDirection;
  pageIndex: number;
  pageSize: number;
}

/** Server-paginated, sortable list of compound-record versions. */
export function useCompoundRecordVersionList({
  conditions,
  sortField,
  direction,
  pageIndex,
  pageSize,
}: UseCompoundRecordVersionListArgs) {
  const { role } = useAuthorization();
  const canAggregate = role === "editor" || role === "admin";
  const orderBy: CompoundRecordVersionsOrderBy[] = [
    { [sortField]: (direction === "asc" ? "ASC" : "DESC") as OrderBy },
    ...(sortField === "id" ? [] : [{ id: "ASC" as OrderBy }]),
  ];
  const result = useSuspenseQuery<
    GetCompoundRecordVersionsListQuery | GetCompoundRecordVersionsForViewerQuery,
    GetCompoundRecordVersionsListQueryVariables
  >(canAggregate ? GetCompoundRecordVersionsListDocument : GetCompoundRecordVersionsForViewerDocument, {
    variables: {
      limit: pageSize + (canAggregate ? 0 : 1),
      offset: pageIndex * pageSize,
      where: buildWhere(conditions),
      orderBy,
    },
  });
  const rows = result.data.compoundRecordVersions.slice(0, pageSize);
  return {
    rows,
    hasNextPage: canAggregate ? undefined : result.data.compoundRecordVersions.length > pageSize,
    // Viewer can read rows but cannot query Hasura aggregates.
    rowCount:
      "compoundRecordVersionsAggregate" in result.data
        ? (result.data.compoundRecordVersionsAggregate.aggregate?.totalCount ?? 0)
        : pageIndex * pageSize + rows.length,
    refetch: result.refetch,
  };
}
